import omit from 'lodash/omit'
import findIndex from 'lodash/findIndex'
import cloneDeep from 'lodash/cloneDeep'
import { vn } from 'vend-number'

import { SALE_VERSION } from '../sale-model.constant'
import { $log } from '../../../ng-compat/imports'

const NS = '[SaleVersioningManager]'

/**
 * @class SaleVersioningManagerService
 */
export class SaleVersioningManagerService {
  /**
   * Accepts JSON representation of a sale and performs the necessary migrations to transition it from and old model
   * version to the latest.
   *
   * @method convertToLatestModelVersion
   *
   * @param {Object} saleJson
   *        The JSON representation of a sale.
   *
   * @returns {Object} The latest model representation of the provided sale.
   */
  public convertToLatestModelVersion(saleJson: any) {
    if (!saleJson || !this._saleModelRequiresMigration(saleJson)) {
      return saleJson
    }

    if (saleJson.version < 11 && !saleJson.saleItems) {
      $log.debug(`${NS} Sale is missing sale items`)
      return
    }

    if (!saleJson.lineItems) {
      $log.debug(`${NS} Sale is missing line items`)
      return
    }

    let saleModelVersion = saleJson.version || 0
    let updatedSale = cloneDeep(saleJson)

    // Chain the migrate functions and set saleModelVersion to the next version after each converting
    if (saleModelVersion === 0) {
      this._migrateInventoryItemData(updatedSale)
      saleModelVersion = 1
    }
    if (saleModelVersion === 1) {
      this._migrateAdjustedProp(updatedSale)
      saleModelVersion = 2
    }
    if (saleModelVersion === 2) {
      this._migrateIsReturnProp(updatedSale)
      saleModelVersion = 3
    }
    if (saleModelVersion === 3) {
      this._migrateProductId(updatedSale)
      saleModelVersion = 4
    }
    if (saleModelVersion === 4) {
      this._migrateGiftCardProps(updatedSale)
      saleModelVersion = 5
    }
    if (saleModelVersion === 5) {
      this._migratePriceProps(updatedSale)
      saleModelVersion = 6
    }
    if (saleModelVersion === 6) {
      this._migrateTaxProps(updatedSale)
      saleModelVersion = 7
    }
    if (saleModelVersion === 7) {
      this._migratePromotion(updatedSale)
      saleModelVersion = 8
    }
    if (saleModelVersion === 8) {
      this._deleteConfirmedAndQuantity(updatedSale)
      saleModelVersion = 9
    }
    if (saleModelVersion === 9) {
      this._migrateLoyaltyProps(updatedSale)
      saleModelVersion = 10
    }
    if (saleModelVersion === 10) {
      updatedSale = this._removeSaleItemModel(updatedSale)
      saleModelVersion = 11
    }
    if (saleModelVersion === 11) {
      updatedSale = this._fixBigDecimalInGiftCardPayments(updatedSale)
      saleModelVersion = 12
    }

    if (saleModelVersion === 12) {
      updatedSale = this._migrateSalesperson(updatedSale)
      saleModelVersion = 13
    }

    return updatedSale
  }

  /**
   * Determines if the provided sale needs to run sale model migrations.
   *
   * @private
   * @method _saleModelRequiresMigration
   *
   * @param {Object} saleJson
   *        The JSON representation of a sale.
   *
   * @returns {Boolean} True if the sale does need to run migrations, false otherwise.
   */
  private _saleModelRequiresMigration(saleJson: any) {
    return !saleJson.version || saleJson.version < SALE_VERSION
  }

  /**
   * Removes `inventoryItem` from each `saleItem`, moving the `product_id` up so its a direct child of `saleItem`.
   *
   * @private
   * @method _migrateInventoryItemData
   *
   * @param {Object} saleJson
   *        The JSON representation of a sale.
   */
  private _migrateInventoryItemData(saleJson: any) {
    const updatedSaleItems = saleJson.saleItems.map((saleItem: $TSFixMe) => {
      if (saleItem.inventoryItem) {
        saleItem.product_id = saleItem.inventoryItem.product_id
        saleItem = omit(saleItem, 'inventoryItem')
      }

      return saleItem
    })

    saleJson.saleItems = updatedSaleItems
  }

  /**
   * Move `adjusted` from each `saleItem` to `lineItem`.
   *
   * @private
   * @method _migrateAdjustedProp
   *
   * @param {Object} saleJson
   *        The JSON representation of a sale.
   */
  private _migrateAdjustedProp(saleJson: any) {
    const { saleItems, lineItems } = saleJson
    lineItems.forEach((lineItem: $TSFixMe) => {
      const index = findIndex(saleItems, {
        localId: lineItem.saleItem.localId,
      })

      if (index >= 0) {
        lineItem.adjusted = saleItems[index].adjusted
        saleItems[index] = omit(saleItems[index], 'adjusted')
      }
    })
  }

  /**
   * Move `isReturn` from each `saleItem` to `lineItem`.
   *
   * @private
   * @method _migrateIsReturnProp
   *
   * @param {Object} saleJson
   *        The JSON representation of a sale.
   */
  private _migrateIsReturnProp(saleJson: any) {
    const { saleItems, lineItems } = saleJson
    lineItems.forEach((lineItem: $TSFixMe) => {
      const index = findIndex(saleItems, {
        localId: lineItem.saleItem.localId,
      })

      if (index >= 0) {
        lineItem.isReturn = saleItems[index].isReturn
        saleItems[index] = omit(saleItems[index], 'isReturn')
      }
    })
  }

  /**
   * Move `product_id` from each `saleItem` to `lineItem`.
   *
   * @private
   * @method _migrateProductId
   *
   * @param {Object} saleJson
   *        The JSON representation of a sale.
   */
  private _migrateProductId(saleJson: any) {
    const { saleItems, lineItems } = saleJson
    lineItems.forEach((lineItem: $TSFixMe) => {
      const index = findIndex(saleItems, {
        localId: lineItem.saleItem.localId,
      })

      if (index >= 0) {
        lineItem.product_id = saleItems[index].product_id
        saleItems[index] = omit(saleItems[index], 'product_id')
      }
    })
  }

  /**
   * Move gift card related props from each `saleItem` to `lineItem`.
   *
   * @private
   * @method _migrateGiftCardProps
   *
   * @param {Object} saleJson
   *        The JSON representation of a sale.
   */
  private _migrateGiftCardProps(saleJson: any) {
    const { saleItems, lineItems } = saleJson
    lineItems.forEach((lineItem: $TSFixMe) => {
      const index = findIndex(saleItems, {
        localId: lineItem.saleItem.localId,
      })

      if (index >= 0) {
        lineItem.giftCardNumber = saleItems[index].giftCardNumber
        lineItem.giftCardReload = saleItems[index].giftCardReload
        lineItem.giftCardClientId = saleItems[index].giftCardClientId
        lineItem.giftCardExpiresAt = saleItems[index].giftCardExpiresAt
        saleItems[index] = omit(saleItems[index], 'giftCardNumber')
        saleItems[index] = omit(saleItems[index], 'giftCardReload')
        saleItems[index] = omit(saleItems[index], 'giftCardClientId')
        saleItems[index] = omit(saleItems[index], 'giftCardExpiresAt')
      }
    })
  }

  /**
   * Move price related props from each `saleItem` to `lineItem`.
   *
   * @private
   * @method _migratePriceProps
   *
   * @param {Object} saleJson
   *        The JSON representation of a sale.
   */
  private _migratePriceProps(saleJson: any) {
    const { saleItems, lineItems } = saleJson
    lineItems.forEach((lineItem: $TSFixMe) => {
      const index = findIndex(saleItems, {
        localId: lineItem.saleItem.localId,
      })

      if (index >= 0) {
        lineItem.price = saleItems[index].price
        lineItem.basePrice = saleItems[index].basePrice
        saleItems[index] = omit(saleItems[index], 'price')
        saleItems[index] = omit(saleItems[index], 'basePrice')
      }
    })
  }

  /**
   * Move tax related props from each `saleItem` to `lineItem`.
   *
   * @private
   * @method _migrateTaxProps
   *
   * @param {Object} saleJson
   *        The JSON representation of a sale.
   */
  private _migrateTaxProps(saleJson: any) {
    const { saleItems, lineItems } = saleJson
    lineItems.forEach((lineItem: $TSFixMe) => {
      const index = findIndex(saleItems, {
        localId: lineItem.saleItem.localId,
      })

      if (index >= 0) {
        lineItem.tax = saleItems[index].tax
        lineItem.baseTax = saleItems[index].baseTax
        lineItem.tax_id = saleItems[index].tax_id
        saleItems[index] = omit(saleItems[index], 'tax')
        saleItems[index] = omit(saleItems[index], 'baseTax')
        saleItems[index] = omit(saleItems[index], 'tax_id')
      }
    })
  }

  /**
   * Move promotion prop from each `saleItem` to `lineItem`.
   *
   * @private
   * @method _migratePromotion
   *
   * @param {Object} saleJson
   *        The JSON representation of a sale.
   */
  private _migratePromotion(saleJson: any) {
    const { saleItems, lineItems } = saleJson
    lineItems.forEach((lineItem: $TSFixMe) => {
      const index = findIndex(saleItems, {
        localId: lineItem.saleItem.localId,
      })

      if (index >= 0) {
        lineItem.promotion = saleItems[index].promotion
        saleItems[index] = omit(saleItems[index], 'promotion')
      }
    })
  }

  /**
   * Delete confirmed & quantity props from each `saleItem`.
   *
   * @private
   * @method _deleteConfirmedAndQuantity
   *
   * @param {Object} saleJson
   *        The JSON representation of a sale.
   */
  private _deleteConfirmedAndQuantity(saleJson: any) {
    const { saleItems, lineItems } = saleJson
    lineItems.forEach((lineItem: $TSFixMe) => {
      const index = findIndex(saleItems, {
        localId: lineItem.saleItem.localId,
      })

      if (index >= 0) {
        saleItems[index] = omit(saleItems[index], 'confirmed')
        saleItems[index] = omit(saleItems[index], 'quantity')
      }
    })
  }

  /**
   * Move loyalty related props from each `saleItem` to `lineItem`.
   *
   * @private
   * @method _migrateLoyaltyProps
   *
   * @param {Object} saleJson
   *        The JSON representation of a sale.
   */
  private _migrateLoyaltyProps(saleJson: any) {
    const { saleItems, lineItems } = saleJson
    lineItems.forEach((lineItem: $TSFixMe) => {
      const index = findIndex(saleItems, {
        localId: lineItem.saleItem.localId,
      })

      if (index >= 0) {
        lineItem.loyaltyValue = saleItems[index].loyaltyValue
        lineItem.explicitLoyaltyValue = saleItems[index].explicitLoyaltyValue
        lineItem.bestPriceBookInfo = saleItems[index].bestPriceBookInfo
        saleItems[index] = omit(saleItems[index], [
          'loyaltyValue',
          'explicitLoyaltyValue',
          'bestPriceBookInfo',
        ])
      }
    })
  }

  /**
   * Remove saleItem from sale model.
   *
   * @private
   * @method _removeSaleItemModel
   *
   * @param {Object} saleJson
   *        The JSON representation of a sale.
   */
  private _removeSaleItemModel(saleJson: any) {
    saleJson.lineItems = saleJson.lineItems.map((lineItem: $TSFixMe) =>
      omit(lineItem, 'saleItem')
    )
    return omit(saleJson, 'saleItems')
  }

  /**
   * Fixes payment amounts stored as a BigDecimal.
   *
   * @private
   * @method _fixBigDecimalInGiftCardPayments
   *
   * @param {Object} saleJson
   *        The JSON representation of a sale.
   */
  private _fixBigDecimalInGiftCardPayments(saleJson: any) {
    if (!saleJson.payments) {
      return saleJson
    }
    saleJson.payments = saleJson.payments.map((payment: $TSFixMe) => {
      if (typeof payment.amount !== 'object') {
        return payment
      }
      const v = vn(0)
      v.s = payment.amount.s
      v.e = payment.amount.e
      v.c = payment.amount.c
      return { ...payment, amount: v.toNumber() }
    })
    return saleJson
  }

  /**
   * Migrates `user` > `salesperson` on line items in a sale
   *
   * @private
   * @param {Object} saleJson
   *        The JSON representation of a sale.
   */
  private _migrateSalesperson(saleJson: any) {
    if (!saleJson.lineItems || saleJson.lineItems.length === 0) {
      return saleJson
    }

    saleJson.lineItems = saleJson.lineItems.map((lineItem: $TSFixMe) => {
      lineItem.salesperson = lineItem.user
      omit(lineItem, 'user')
      return lineItem
    })

    return saleJson
  }
}
